import { useEffect, useState, type ReactElement } from 'react';

import { Button, buttonVariants } from '@docuid/ui/components/ui/button';
import { cn } from '@docuid/ui/lib/utils';

import { APP_SOURCE_URL, getDashboardLoginUrl, getMarketingUrl } from './constants';
import { MenuToggleIcon } from './MenuToggleIcon';
import { MicrosoftLogo } from './OfficeLogos';
import { ThemeMenu } from './ThemeMenu';
import { useScroll } from './use-scroll';

export function LandingHeader(): ReactElement {
  const [open, setOpen] = useState(false);
  const scrolled = useScroll(10);
  const loginUrl = getDashboardLoginUrl();
  const links = [
    { label: 'Hosts', href: '#hosts' },
    { label: 'How it opens', href: '#how-it-opens' },
    { label: 'Website', href: getMarketingUrl() },
  ] as const;

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <header
      className={cn(
        'sticky top-0 z-50 mx-auto w-full max-w-5xl border-b border-transparent md:rounded-md md:border md:transition-all md:ease-out',
        {
          'bg-background/95 supports-[backdrop-filter]:bg-background/50 border-border backdrop-blur-lg md:top-4 md:max-w-4xl md:shadow':
            scrolled && !open,
          'bg-background/90': open,
        },
      )}
    >
      <nav
        className={cn(
          'flex h-14 w-full items-center justify-between px-4 md:h-12 md:transition-all md:ease-out',
          { 'md:px-2': scrolled },
        )}
      >
        <a href="/" className="flex items-center">
          <img
            src="/assets/logo-transparent-bg.png"
            alt="DocuID"
            className="h-5 w-auto dark:brightness-0 dark:invert"
          />
        </a>
        <div className="hidden items-center gap-1 md:flex">
          {links.map((link) => (
            <a key={link.label} className={buttonVariants({ variant: 'ghost' })} href={link.href}>
              {link.label}
            </a>
          ))}
          <ThemeMenu />
          <a
            href={APP_SOURCE_URL}
            target="_blank"
            rel="noreferrer"
            className={buttonVariants({ variant: 'outline', size: 'sm' })}
          >
            <MicrosoftLogo className="size-3.5" />
            AppSource
          </a>
          <a href={loginUrl} className={buttonVariants({ size: 'sm' })}>
            Log in
          </a>
        </div>
        <div className="flex items-center gap-1 md:hidden">
          <ThemeMenu />
          <Button
            size="icon"
            variant="outline"
            type="button"
            aria-expanded={open}
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => {
              setOpen((isOpen) => !isOpen);
            }}
          >
            <MenuToggleIcon open={open} className="size-5" duration={300} />
          </Button>
        </div>
      </nav>

      <div
        className={cn(
          'bg-background/90 fixed top-14 right-0 bottom-0 left-0 z-50 flex flex-col overflow-hidden border-y md:hidden',
          open ? 'block' : 'hidden',
        )}
      >
        <div
          data-slot={open ? 'open' : 'closed'}
          className="data-[slot=open]:animate-in data-[slot=open]:zoom-in-95 flex h-full w-full flex-col justify-between gap-y-2 p-4 ease-out"
        >
          <div className="grid gap-y-2">
            {links.map((link) => (
              <a
                key={link.label}
                className={buttonVariants({ variant: 'ghost', className: 'justify-start' })}
                href={link.href}
                onClick={() => {
                  setOpen(false);
                }}
              >
                {link.label}
              </a>
            ))}
          </div>
          <div className="flex flex-col gap-2">
            <a
              href={APP_SOURCE_URL}
              target="_blank"
              rel="noreferrer"
              className={buttonVariants({ variant: 'outline', className: 'w-full' })}
            >
              <MicrosoftLogo className="size-3.5" />
              Get it from Microsoft AppSource
            </a>
            <a href={loginUrl} className={buttonVariants({ className: 'w-full' })}>
              Log in
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
